import type { StripeMarks } from "../content/trials";
import { onTrack, paceTarget } from "../game/round";
import "./TrialProgress.css";

interface Props {
  score: number;
  passMark: number;
  stripes: StripeMarks;
  seconds: number;
  secondsLeft: number;
}

/**
 * The trial bar: fills with right answers, with ticks at each stripe mark, a
 * flag at the pass mark and a chevron at an even pace. Display only: it never
 * changes the questions or the score.
 */
export function TrialProgress({ score, passMark, stripes, seconds, secondsLeft }: Props) {
  const pct = (n: number) => `${Math.min(100, (n / passMark) * 100)}%`;
  const pace = paceTarget(passMark, seconds, secondsLeft);
  const passed = score >= passMark;
  const ahead = onTrack(score, passMark, seconds, secondsLeft);
  const earned = stripes.filter((m) => score >= m).length;
  return (
    <div className="tprog">
      <div
        className="tprog-bar"
        role="progressbar"
        aria-label="Trial progress"
        aria-valuemin={0}
        aria-valuemax={passMark}
        aria-valuenow={Math.min(score, passMark)}
        aria-valuetext={`${score} of ${passMark}, ${earned} of 3 stripes`}
      >
        <div className={"tprog-fill" + (passed ? " passed" : "")} style={{ width: pct(score) }} />
        {stripes.map((m) => (
          <span
            key={m}
            className={"tprog-tick" + (score >= m ? " on" : "")}
            style={{ left: pct(m) }}
            aria-hidden="true"
          />
        ))}
        {!passed && <span className="tprog-pace" style={{ left: pct(pace) }} aria-hidden="true" />}
        <span className="tprog-flag display" aria-hidden="true">
          {passMark}
        </span>
      </div>
      <div className="tprog-label" aria-live="off">
        {passed ? "Pass mark reached!" : ahead ? "On track" : "Keep going"}
      </div>
    </div>
  );
}
